import React, { useState } from 'react';
import { useEntityList } from '@/hooks/useEntityList';
import { exportEntityCsv } from '@/lib/csv';
import { mapMember } from '../members';
import { useUser } from '@/contexts/UserContext';
import { SectionLayout } from '@/components/section-layout';
import { Download } from 'lucide-react';
import { GenericTag } from '@/styles/GenericTag';
import { trackUserUpdated, trackButtonClick, trackFormSubmission } from '@/lib/posthog-events';
import { getApiUrl } from '@/lib/api-config';
import { GenericTable, GenericTableColumn } from '@/styles/GenericTable';
import { SearchButton, ThreeDotMenuButton } from '@/styles/StandardButtons';
import { TeamForm } from './components/TeamForm';

interface TeamMember {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  title: string;
  address: string;
  city: string;
  state: string;
  country: string;
  zip_code: string;
  role: string;
  is_active: boolean;
  notes: string;
  created_at: string;
  attachment_count?: number;
}

export function TeamPage() {
  const { companyId, user, userRole } = useUser();
  // The list: ONE server-driven query (search + sort + page) over the company's admins and members -- the database searches, sorts and
  // pages everyone the user may see; each person's document counter comes with the row (hooks/useEntityList.ts).
  const list = useEntityList<any, TeamMember>({
    entityType: 'team_member',
    enabled: !!companyId,
    mode: 'pages',
    includeCounts: true,
    defaultSort: { columnKey: 'first_name', direction: 'asc', sortKey: 'name' },
    mapRow: mapMember,
  });
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTeamMember, setEditingTeamMember] = useState<TeamMember | null>(null);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);

  const handleEditTeamMember = (teamMember: TeamMember) => {
    trackButtonClick('edit_team_member_button', { 
      context: 'team_management',
      user_id: teamMember.id,
      user_name: `${teamMember.first_name} ${teamMember.last_name}`
    });
    setEditingTeamMember(teamMember);
    setIsDialogOpen(true);
  };

  const parseErrorMessage = (error: any): string => {
    // If it's already a user-friendly string, return it
    if (typeof error === 'string') {
      return error;
    }

    // Check for error object with message
    // API returns { success: false, error: "...", details: "...", code: "..." }
    // When thrown as Error, it becomes { message: "...", ... }
    // Supabase errors can be nested, so check both top-level and nested error objects
    const errorMessage = error?.message || error?.error || '';
    const errorDetails = error?.details || error?.error?.details || '';
    const errorCode = error?.code || error?.error?.code || '';

    // Handle duplicate email errors
    if (
      errorMessage.includes('email') && 
      (errorMessage.includes('unique') || errorMessage.includes('duplicate') || errorCode === '23505')
    ) {
      return 'This email address is already in use. Please use a different email.';
    }

    // Handle unique constraint violations
    if (errorCode === '23505') {
      const detailsStr = String(errorDetails || '').toLowerCase();
      if (detailsStr.includes('email') || detailsStr.includes('users_email_unique')) {
        return 'This email address is already in use. Please use a different email.';
      }
      if (errorDetails?.includes('user_id') || errorDetails?.includes('company_id')) {
        return 'This team member is already associated with this company.';
      }
      return 'This record already exists. Please check for duplicates.';
    }

    // Handle not-null constraint violations
    if (errorCode === '23502') {
      const fieldMatch = errorDetails?.match(/column "(\w+)" of relation/);
      if (fieldMatch) {
        const fieldName = fieldMatch[1];
        const friendlyName = fieldName
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (l: string) => l.toUpperCase());
        return `${friendlyName} is required.`;
      }
      return 'A required field is missing. Please check all required fields.';
    }

    // Handle foreign key violations
    if (errorCode === '23503') {
      return 'Invalid reference. Please check that all related records exist.';
    }

    // Handle check constraint violations
    if (errorCode === '23514') {
      if (errorMessage.includes('email')) {
        return 'Please enter a valid email address.';
      }
      if (errorMessage.includes('status') || errorMessage.includes('role')) {
        return 'Invalid status or role value. Please select a valid option.';
      }
      return 'Invalid data format. Please check your input.';
    }

    // Handle specific error messages from API
    if (errorMessage.includes('user_id and company_id are required')) {
      return 'Session expired. Please refresh the page and try again.';
    }

    if (errorMessage.includes('Failed to create user') || errorMessage.includes('Failed to update user')) {
      const detailsStr = String(errorDetails || '').toLowerCase();
      if (detailsStr.includes('email') || detailsStr.includes('users_email_unique') || 
          detailsStr.includes('duplicate key') || detailsStr.includes('unique constraint')) {
        return 'Failed to save team member. This email address is already in use. Please use a different email.';
      }
      return 'Failed to save team member. Please check all fields and try again.';
    }

    if (errorMessage.includes('Failed to update user role')) {
      return 'Failed to update team member role. Please try again.';
    }

    // Handle generic "Failed to create user" or "Failed to update user" messages - check if it's a duplicate email
    if (errorMessage.includes('Failed to create') || errorMessage.includes('Failed to update')) {
      const detailsStr = String(errorDetails || '').toLowerCase();
      if (detailsStr.includes('email') || detailsStr.includes('users_email_unique') || 
          detailsStr.includes('duplicate key') || detailsStr.includes('unique constraint')) {
        if (detailsStr.includes('email') || detailsStr.includes('users_email_unique')) {
          return 'Failed to save team member. This email address is already in use. Please use a different email.';
        }
      }
    }

    // Return the original error message if we can't parse it
    return errorMessage || 'An unexpected error occurred. Please try again.';
  };

  const handleSaveTeamMember = async (formData: any) => {
    if (!editingTeamMember) return;

    try {
      // 1. The acting user and the company come from the session (the server re-derives both)
      const userId = user?.id;
      if (!userId) throw new Error("User not logged in");
      if (!companyId) throw new Error("No active company found for this user");

      // 3. Update team member in users table. People > Team can only EDIT
      // an existing member's profile -- adding/inviting a new team member
      // is exclusively a Settings > Users flow (see UsersTab.tsx's
      // /api/people/invite, which creates a real auth.users login). Team's
      // own former "create" branch here POSTed straight to
      // /api/entities/user + /api/entities/user_company_relation, which
      // never created an auth account -- a broken, inconsistent duplicate
      // of the invite flow. Removed entirely rather than left dead/
      // reachable.
      const userData = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone,
        title: formData.title,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        country: formData.country,
        zip_code: formData.zip_code,
        // phase53_drop_obsolete_user_columns.sql (2026-09-09): users.status
        // used to be written here too, but nothing ever read it (confirmed
        // dead) -- `user_company_relation.is_active`, set via the
        // /api/user-role/:id call right below, is and remains the real
        // source of truth for active/inactive.
        notes: formData.notes,
      };

      // Update existing user via generic API
      const response = await fetch(getApiUrl(`api/entities/user/${editingTeamMember.id}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          company_id: companyId,
          ...userData
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        const parsedError = parseErrorMessage(errorData);
        throw new Error(parsedError);
      }

      // Update the relationship status and role via dedicated endpoint
      const relationResponse = await fetch(getApiUrl(`api/user-role/${editingTeamMember.id}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          company_id: companyId,
          role: formData.role,
          is_active: formData.is_active
        })
      });

      if (!relationResponse.ok) {
        const errorData = await relationResponse.json();
        const parsedError = parseErrorMessage(errorData);
        throw new Error(parsedError);
      }

      // Track successful user update with PostHog
      trackUserUpdated({
        user_id: editingTeamMember.id,
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        role: formData.role,
        is_active: formData.is_active,
        company_id: companyId,
        updated_by: userId
      });

      // Track successful form submission
      trackFormSubmission({
        form_type: 'user_update',
        success: true
      });

      // Reset form + refresh team members
      setIsDialogOpen(false);
      setEditingTeamMember(null);
      list.reload();

    } catch (error: any) {
      console.error("Error saving team member:", error);
      
      // Parse error message for better user experience
      const userFriendlyError = parseErrorMessage(error);
      
      // Track failed form submission with PostHog
      trackFormSubmission({
        form_type: 'user_update',
        success: false,
        error_message: userFriendlyError
      });

      throw error; // Re-throw so TeamForm can handle it
    }
  };

  // No handleDeleteSelected here on purpose: People > Team must never be
  // able to remove a team member (or add one -- see handleSaveTeamMember
  // above). Removing a user account is exclusively a Settings > Users
  // action (UsersTab.tsx's handleDeleteUser, same soft_delete_user()
  // endpoint). selectedRows/checkboxes stay -- Export still needs them.

  // Export to CSV: the team members selected in the table, or -- with nothing selected -- EVERY member the current search matches,
  // read by the backend (not only the page that is showing).
  const handleExportSelected = () => exportEntityCsv(list.exportRows, selectedRows, 'team-members', 'team members');

  // Helper functions for formatting
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  // Map role to GenericTag role types
  const mapRoleToTag = (role: string): 'admin' | 'member' => {
    return role === 'admin' ? 'admin' : 'member';
  };

  // Map active/inactive to GenericTag status types
  const mapActiveStatusToTag = (isActive: boolean): 'active' | 'inactive' => {
    return isActive ? 'active' : 'inactive';
  };

  // Column definitions for GenericTable
  const columns: GenericTableColumn<TeamMember>[] = [
    {
      key: 'first_name',
      header: 'Name',
      width: '8rem',
      sortable: true,
      sortKey: 'name',
      render: (row) => (
        <span className="font-medium">
          {row.first_name} {row.last_name}
        </span>
      ),
    },
    {
      key: 'title',
      header: 'Title',
      width: '4rem',
      sortable: true,
      sortKey: 'title',
      render: (row) => row.title || '-',
    },
    {
      key: 'email',
      header: 'Email',
      width: '10rem',
      sortable: true,
      sortKey: 'email',
      render: (row) => row.email,
    },
    {
      key: 'role',
      header: 'Role',
      width: '4.5rem',
      sortable: true,
      sortKey: 'role',
      render: (row) => (
        <GenericTag 
          type="role" 
          value={mapRoleToTag(row.role)}
        />
      ),
    },
    {
      key: 'is_active',
      header: 'Status',
      width: '4.5rem',
      sortable: true,
      sortKey: 'is_active',
      render: (row) => (
        <GenericTag 
          type="status" 
          value={mapActiveStatusToTag(row.is_active)}
        />
      ),
    },
    {
      key: 'created_at',
      header: 'Created',
      width: '5rem',
      sortable: true,
      sortKey: 'created_at',
      render: (row) => formatDate(row.created_at),
    },
    {
      key: 'attachment_count',
      header: 'Attachments',
      width: '6.25rem',
      align: 'center',
      sortable: true,
      sortKey: 'attachment_count',
      render: (row) => (
        <span className="font-medium">{row.attachment_count || 0}</span>
      ),
    },
  ];

  // Menu items for ThreeDotMenuButton. No "New"/"Delete" here on purpose --
  // Team can only view/export/edit; adding and removing a team member are
  // Settings > Users-only actions (see handleSaveTeamMember above). "Import"
  // was a no-op placeholder wired to nothing and is removed rather than
  // left as a dead affordance implying an add flow that doesn't exist.
  const menuItems = [
    {
      label: 'Export',
      icon: <Download className="w-4 h-4" />,
      onClick: handleExportSelected,
    },
  ];

  // Don't block page - let table show with skeleton rows

  return (
    <SectionLayout title="Team">
      <div className="space-y-6">
        {/* Action Buttons */}
        <div className="flex items-center justify-end">
          <div className="flex items-center gap-2">
            <SearchButton
              onSearch={list.setSearchInput}
              placeholder="Search"
            />
            <ThreeDotMenuButton items={menuItems} />
          </div>
        </div>

        {/* Generic Table */}
        <GenericTable
          columns={columns}
          {...list.tableProps}
          rowKey={(row) => row.id}
          scrollContainerMaxWidth="calc(100vw - 19.2rem)"
          minTableWidth={1200}
          onRowClick={handleEditTeamMember}
          selectable={true}
          selectedKeys={selectedRows}
          onSelectionChange={setSelectedRows}
          emptyState="No team members found"
        />

        {/* Add/Edit Team Member Form */}
        <TeamForm
          isOpen={isDialogOpen}
          teamMember={editingTeamMember}
          userRole={userRole || 'member'}
          onClose={() => {
            setIsDialogOpen(false);
            setEditingTeamMember(null);
          }}
          onSave={handleSaveTeamMember}
        />

      </div>
    </SectionLayout>
  );
} 